import React, { useState, useEffect } from 'react';
import { Download, Printer, Building2, Search, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { exportToPDF } from '@/utils/groupReportPDF';

const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

const GroupIncomeExpensesReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedCollege, setSelectedCollege] = useState('');
  const [colleges, setColleges] = useState([]);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchColleges(); fetchReport(); }, []);

  const fetchColleges = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/groups/my-group`, { headers: { Authorization: `Bearer ${token}` } });
      setColleges(res.data.colleges || []);
    } catch (e) { console.error(e); }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/groups/my-group/reports/income-expense?from_date=${fromDate}&to_date=${toDate}`;
      if (selectedCollege) url += `&college_id=${selectedCollege}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const exportToExcel = () => {
    if (!data) return toast.error('No data to export');
    const wb = XLSX.utils.book_new();
    const incWs = XLSX.utils.json_to_sheet((data.all_incomes || []).map(i => ({
      'College': i.college_name, 'Category': i.category, 'Description': i.description,
      'Amount': i.amount, 'Mode': i.payment_mode, 'Date': i.date
    })));
    const expWs = XLSX.utils.json_to_sheet((data.all_expenses || []).map(e => ({
      'College': e.college_name, 'Category': e.category, 'Description': e.description,
      'Amount': e.amount, 'Mode': e.payment_mode, 'Date': e.date
    })));
    XLSX.utils.book_append_sheet(wb, incWs, 'Incomes');
    XLSX.utils.book_append_sheet(wb, expWs, 'Expenses');
    XLSX.writeFile(wb, `Group_Income_Expenses_${fromDate}_${toDate}.xlsx`);
  };

  const exportPDF = () => {
    if (!data) return toast.error('No data to export');
    const rows = [
      ...(data.all_incomes || []).map(i => [i.college_name, 'Income', i.category, i.description, fmt(i.amount), i.payment_mode, i.date]),
      ...(data.all_expenses || []).map(e => [e.college_name, 'Expense', e.category, e.description, fmt(e.amount), e.payment_mode, e.date])
    ];
    if (!rows.length) return toast.error('No data to export');
    exportToPDF('Group Income & Expenses Report',
      ['College', 'Type', 'Category', 'Description', 'Amount', 'Mode', 'Date'],
      rows,
      `Group_Income_Expenses_${fromDate}_${toDate}`,
      `Period: ${fromDate} to ${toDate} | Income: ${fmt(data.totals?.total_income)} | Expense: ${fmt(data.totals?.total_expense)} | Net: ${fmt(data.totals?.net)}`
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="group-income-expenses-report">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Income & Expenses</h1>
          <p className="text-sm text-gray-500">Consolidated income and expenses across all colleges</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="group-ie-excel-btn" onClick={exportToExcel} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"><Download className="h-4 w-4" /> Excel</button>
          <button data-testid="group-ie-pdf-btn" onClick={exportPDF} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"><FileText className="h-4 w-4" /> PDF</button>
          <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"><Printer className="h-4 w-4" /> Print</button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-4 mb-6 flex flex-wrap gap-4 items-end">
        <div><label className="block text-xs font-medium text-gray-600 mb-1">From</label><input data-testid="group-ie-from-date" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">To</label><input data-testid="group-ie-to-date" type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">College</label>
          <select data-testid="group-ie-college-filter" value={selectedCollege} onChange={e => setSelectedCollege(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
            <option value="">All Colleges</option>{colleges.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <button data-testid="group-ie-search-btn" onClick={fetchReport} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2"><Search className="h-4 w-4" /> Search</button>
      </div>

      {loading ? <div className="text-center py-12 text-gray-500">Loading...</div> : data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-green-500">
              <p className="text-xs text-gray-500">Total Income (Group)</p>
              <p data-testid="group-ie-total-income" className="text-2xl md:text-3xl font-bold text-green-600">{fmt(data.totals?.total_income)}</p>
              <p className="text-xs text-gray-400 mt-1">{data.totals?.income_entries || 0} entries</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-red-500">
              <p className="text-xs text-gray-500">Total Expenses (Group)</p>
              <p data-testid="group-ie-total-expense" className="text-2xl md:text-3xl font-bold text-red-600">{fmt(data.totals?.total_expense)}</p>
              <p className="text-xs text-gray-400 mt-1">{data.totals?.expense_entries || 0} entries</p>
            </div>
            <div className={`bg-white rounded-xl shadow p-5 border-l-4 ${(data.totals?.net || 0) >= 0 ? 'border-blue-500' : 'border-orange-500'}`}>
              <p className="text-xs text-gray-500">Net (Income − Expense)</p>
              <p data-testid="group-ie-net" className={`text-2xl md:text-3xl font-bold ${(data.totals?.net || 0) >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>{fmt(data.totals?.net)}</p>
            </div>
          </div>

          {data.colleges?.map(college => (
            <div key={college.college_id} className="bg-white rounded-xl shadow mb-4" data-testid={`group-ie-college-${college.college_id}`}>
              <div className="p-4 border-b flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-blue-600" /><h3 className="font-bold">{college.college_name}</h3>
                  {college.is_external && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">🔗 External</span>}
                  {college.external_error && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Unreachable</span>}
                </div>
                <div className="flex gap-4 text-sm font-semibold">
                  <span className="text-green-600">Income: {fmt(college.total_income)}</span>
                  <span className="text-red-600">Expense: {fmt(college.total_expense)}</span>
                  <span className={college.net >= 0 ? 'text-blue-600' : 'text-orange-600'}>Net: {fmt(college.net)}</span>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x">
                <div>
                  <p className="px-4 pt-3 text-xs font-semibold text-green-700 uppercase">Incomes</p>
                  {college.incomes?.length > 0 ? (
                    <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-gray-50"><tr>
                      <th className="text-left p-3">Category</th><th className="text-left p-3">Date</th><th className="text-right p-3">Amount</th>
                    </tr></thead><tbody className="divide-y">{college.incomes.map((i, idx) => (
                      <tr key={idx} className="hover:bg-gray-50"><td className="p-3">{i.category}</td><td className="p-3">{i.date}</td><td className="p-3 text-right font-medium text-green-700">{fmt(i.amount)}</td></tr>
                    ))}</tbody></table></div>
                  ) : <p className="p-4 text-gray-400 text-sm">No income in this period</p>}
                </div>
                <div>
                  <p className="px-4 pt-3 text-xs font-semibold text-red-700 uppercase">Expenses</p>
                  {college.expenses?.length > 0 ? (
                    <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-gray-50"><tr>
                      <th className="text-left p-3">Category</th><th className="text-left p-3">Date</th><th className="text-right p-3">Amount</th>
                    </tr></thead><tbody className="divide-y">{college.expenses.map((e, idx) => (
                      <tr key={idx} className="hover:bg-gray-50"><td className="p-3">{e.category}</td><td className="p-3">{e.date}</td><td className="p-3 text-right font-medium text-red-700">{fmt(e.amount)}</td></tr>
                    ))}</tbody></table></div>
                  ) : <p className="p-4 text-gray-400 text-sm">No expenses in this period</p>}
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default GroupIncomeExpensesReport;
